/**
 * The export file's own vocabulary.
 *
 * Everything the codec reasons about — the format marker, the layout version,
 * the section names, the field names, the separators — is spelled out here and
 * nowhere else. `EXPORT_FIELD` is the single source of truth for both writing a
 * header row and reading one back: a section cannot be written with one set of
 * names and read with another, because there is only one set.
 *
 * The file is a *projection of what is already stored on the tablet*, not a new
 * data model. Its record shapes are the ones `src/lib/rcu/decode.ts` and
 * `src/features/keypads/decode.ts` already accept; this module only decides how
 * those shapes are laid out in a spreadsheet.
 */

/** Written into the `meta` section so a foreign CSV is refused, not guessed at. */
export const EXPORT_FORMAT = 'toyama.rcu-export';

/**
 * The *file layout* version, bumped when a section or a field changes.
 *
 * Deliberately not the configuration's `SCHEMA_VERSION`: a tablet may be able to
 * read an older file layout and still refuse the record shapes inside it, and the
 * two questions should not be answered by one number.
 */
export const EXPORT_VERSION = 1;

/** Sections of the file, in the order they are written. */
export const EXPORT_SECTION = {
  META: 'meta',
  RCU: 'rcu',
  RAIL: 'rail',
  BRIDGE: 'bridge',
  ROOM: 'room',
  DEFAULT_ROOM: 'defaultRoom',
  LOAD: 'load',
  KEYPAD: 'keypad',
  KEYPAD_KEY: 'keypadKey',
  KEY_MAPPING: 'keyMapping',
  WRITTEN: 'written',
} as const;

export type ExportSection = (typeof EXPORT_SECTION)[keyof typeof EXPORT_SECTION];

/** Fields of each section, in the order the header row spells them. */
export const EXPORT_FIELD = {
  [EXPORT_SECTION.META]: ['format', 'version', 'exportedAt'],
  [EXPORT_SECTION.RCU]: [
    'name',
    'deviceId',
    'serialNumber',
    'ipAddress',
    'location',
    'outputCount',
    'updatedAt',
  ],
  [EXPORT_SECTION.RAIL]: ['id', 'type', 'address', 'label'],
  [EXPORT_SECTION.BRIDGE]: ['id', 'address', 'master', 'label'],
  [EXPORT_SECTION.ROOM]: ['id', 'name', 'note', 'createdAt'],
  [EXPORT_SECTION.DEFAULT_ROOM]: ['roomId'],
  [EXPORT_SECTION.LOAD]: [
    'id',
    'name',
    'type',
    'roomId',
    'channelKind',
    'channelRailId',
    'channelNumber',
    'wattage',
    'iconKey',
    'createdAt',
  ],
  [EXPORT_SECTION.KEYPAD]: [
    'id',
    'type',
    'name',
    'address',
    'cat6Branch',
    'location',
    'retiredAddresses',
    'createdAt',
  ],
  [EXPORT_SECTION.KEYPAD_KEY]: ['keypadId', 'id', 'index', 'name'],
  [EXPORT_SECTION.KEY_MAPPING]: ['keypadId', 'keyId', 'state', 'targetId', 'value'],
  [EXPORT_SECTION.WRITTEN]: ['trigger', 'fingerprint'],
} as const satisfies Record<ExportSection, readonly string[]>;

/** The order sections are written in: what the device is, then what is fitted. */
export const EXPORT_SECTION_ORDER = [
  EXPORT_SECTION.META,
  EXPORT_SECTION.RCU,
  EXPORT_SECTION.RAIL,
  EXPORT_SECTION.BRIDGE,
  EXPORT_SECTION.ROOM,
  EXPORT_SECTION.DEFAULT_ROOM,
  EXPORT_SECTION.LOAD,
  EXPORT_SECTION.KEYPAD,
  EXPORT_SECTION.KEYPAD_KEY,
  EXPORT_SECTION.KEY_MAPPING,
  EXPORT_SECTION.WRITTEN,
] as const satisfies readonly ExportSection[];

/** Fields of the `meta` row, which is read before anything else is trusted. */
export const META_FIELD = {
  FORMAT: 'format',
  VERSION: 'version',
  EXPORTED_AT: 'exportedAt',
} as const;

/** How a list inside one cell is spelled. Only ever joined over numbers. */
export const LIST_SEPARATOR = '|';

/** RFC 4180's own characters. Named so the codec never spells one out twice. */
export const CSV_DELIMITER = ',';
export const CSV_QUOTE = '"';
export const CSV_ESCAPED_QUOTE = '""';
export const CSV_NEWLINE = '\r\n';

/** A byte-order mark, which Excel writes and no reader here wants. */
export const BYTE_ORDER_MARK = '\uFEFF';

/** What the file is called, and what a browser is told it is. */
export const EXPORT_FILE = {
  EXTENSION: 'csv',
  MIME_TYPE: 'text/csv;charset=utf-8',
  /** Sanitised to this before it reaches a filename. */
  SAFE_NAME: /[^A-Za-z0-9._-]/g,
  EMPTY_NAME: 'unnamed',
  PREFIX: 'toyama',
  /** A filename with no usable identity in it, e.g. a blank device id. */
  FALLBACK_NAME: 'rcu',
} as const;

/** Bounds the codec owns, so a huge file is refused rather than chewed on. */
export const EXPORT_LIMITS = {
  /** Four megabytes of CSV is far more than this configuration can ever be. */
  MAX_FILE_BYTES: 4 * 1024 * 1024,
  /** Bytes in a kibibyte, for reading a size back as something a person reads. */
  BYTES_PER_KB: 1024,
  /** Decimal places a size is reported to, e.g. `4.0 MB`. */
  SIZE_DECIMALS: 1,
  /**
   * How many rows one section may hold before the file is treated as hostile.
   * Well above any real installation: 60 modules, 26 outputs, and panels.
   */
  MAX_SECTION_ROWS: 10_000,
} as const;

/** The `meta` fields a section header must not be confused with. */
export const EXPORT_HEADER_OFFSET = 1;
